// AGC: Admin Panel - Weight Distribution Component
// AGC: Visual editor for algorithm weight percentages with validation

import React from 'react'

interface WeightDistributionProps {
  weights: Record<string, number>
  labels: Record<string, string>
  colors: string[]
  onChange: (key: string, value: number) => void
}

/**
 * AGC: WeightDistribution - Visual weight editor with horizontal bar
 * AGC: Shows weight distribution and validates sum equals 100%
 */
export const WeightDistribution: React.FC<WeightDistributionProps> = ({
  weights,
  labels,
  colors,
  onChange,
}) => {
  const total = Object.values(weights).reduce((a, b) => a + b, 0)
  const isValid = Math.abs(total - 1) < 0.01

  return (
    <div className={`bg-slate-50 dark:bg-white/[0.02] rounded-xl p-5 border ${isValid ? 'border-slate-200 dark:border-white/5' : 'border-red-400'}`}>
      <div className="flex justify-between mb-4">
        <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Distribuição de Pesos</h4>
        <span className={`text-sm font-semibold ${isValid ? 'text-green-600' : 'text-red-500'}`}>
          Total: {(total * 100).toFixed(0)}%
          {!isValid && ' ⚠️'}
        </span>
      </div>

      {/* AGC: Visual distribution bar */}
      <div className="h-8 rounded-lg flex overflow-hidden mb-5">
        {Object.entries(weights).map(([key, value], i) => (
          <div
            key={key}
            className="flex items-center justify-center text-white text-xs font-semibold transition-all duration-300"
            style={{
              width: `${value * 100}%`,
              backgroundColor: colors[i % colors.length],
            }}
          >
            {value >= 0.08 && `${(value * 100).toFixed(0)}%`}
          </div>
        ))}
      </div>

      {/* AGC: Individual weight sliders */}
      {Object.entries(weights).map(([key, value], i) => (
        <div key={key} className="mb-4">
          <div className="flex justify-between mb-1.5">
            <span className="text-sm text-slate-600 dark:text-slate-300 flex items-center gap-2">
              <span
                className="w-3 h-3 rounded"
                style={{ backgroundColor: colors[i % colors.length] }}
              />
              {labels[key] || key}
            </span>
            <span className="text-sm font-semibold">{(value * 100).toFixed(0)}%</span>
          </div>
          <input
            type="range"
            min={0}
            max={100}
            value={value * 100}
            onChange={(e) => onChange(key, Number(e.target.value) / 100)}
            className="w-full h-1.5 bg-slate-200 dark:bg-white/10 rounded-full appearance-none cursor-pointer"
            style={{ accentColor: colors[i % colors.length] }}
          />
        </div>
      ))}

      {/* AGC: Warning message when invalid */}
      {!isValid && (
        <div className="mt-4 p-3 bg-error-50 dark:bg-red-500/15 border border-red-200 dark:border-red-500/30 rounded-lg">
          <p className="text-sm text-error-700 dark:text-red-400">
            ⚠️ Os pesos devem somar 100%. Ajuste os valores para totalizar 100%.
          </p>
        </div>
      )}
    </div>
  )
}

export default WeightDistribution


